/**
 * @file kpiDrilldownConfig.ts
 * @description Maps a KPI card id to the title/columns shown in its
 * click-through KpiDetailModal popup. Only KPIs listed here are wired to
 * open the modal (see DashboardHome.tsx) — unlisted KPIs stay non-clickable
 * until their backend drilldown case (analytics.service.ts#getKpiDrilldown)
 * and an entry here are both added.
 *
 * To add a new KPI's drill-down:
 * 1. Add a `case` to the switch in getKpiDrilldown (backend/.../analytics.service.ts).
 * 2. Add an entry below with a title and the columns to render.
 * 3. Add `onClick` to the corresponding <StatCard> in DashboardHome.tsx.
 */

export interface DrilldownColumn {
  key: string;
  label: string;
}

export interface KpiDrilldownConfig {
  title: string;
  columns: DrilldownColumn[];
}

const ASSET_COLUMNS: DrilldownColumn[] = [
  { key: 'assetTag', label: 'Asset Tag' },
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'status', label: 'Status' },
  { key: 'assignedTo', label: 'Assigned To' },
];

const ASSIGNMENT_COLUMNS: DrilldownColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'name', label: 'Item' },
  { key: 'assignedTo', label: 'Assigned To' },
  { key: 'status', label: 'Status' },
  { key: 'dueDate', label: 'Due Date' },
];

const LICENSE_COLUMNS: DrilldownColumn[] = [
  { key: 'name', label: 'Software' },
  { key: 'plan', label: 'Plan' },
  { key: 'seats', label: 'Seats Used/Total' },
  { key: 'expiryDate', label: 'Expiry Date' },
];

const INVENTORY_COLUMNS: DrilldownColumn[] = [
  { key: 'name', label: 'Item' },
  { key: 'availableStock', label: 'Available' },
  { key: 'minStockLevel', label: 'Min Level' },
  { key: 'totalStock', label: 'Total Stock' },
  { key: 'status', label: 'Status' },
];

const WARRANTY_COLUMNS: DrilldownColumn[] = [
  { key: 'assetTag', label: 'Asset Tag' },
  { key: 'name', label: 'Name' },
  { key: 'warrantyExpiry', label: 'Warranty Expiry' },
  { key: 'status', label: 'Status' },
  { key: 'assignedTo', label: 'Assigned To' },
];

const NEW_ASSET_COLUMNS: DrilldownColumn[] = [
  { key: 'assetTag', label: 'Asset Tag' },
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'purchaseCost', label: 'Purchase Cost' },
  { key: 'purchaseDate', label: 'Purchase Date' },
];

const BOOK_VALUE_COLUMNS: DrilldownColumn[] = [
  { key: 'assetTag', label: 'Asset Tag' },
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'currentValue', label: 'Book Value' },
  { key: 'purchaseCost', label: 'Purchase Cost' },
];

const RENTED_ASSET_COLUMNS: DrilldownColumn[] = [
  { key: 'assetTag', label: 'Asset Tag' },
  { key: 'name', label: 'Name' },
  { key: 'monthlyRent', label: 'Monthly Rent' },
  { key: 'assignedTo', label: 'Assigned To' },
];

const RENTAL_COST_COLUMNS: DrilldownColumn[] = [
  { key: 'assetTag', label: 'Asset Tag' },
  { key: 'name', label: 'Name' },
  { key: 'rentalStartDate', label: 'Rental Start' },
  { key: 'disposalDate', label: 'Dispose Date' },
  { key: 'monthsBilled', label: 'Months Billed' },
  { key: 'monthlyRent', label: 'Monthly Rent' },
  { key: 'totalCost', label: 'Total Cost' },
  { key: 'assignedTo', label: 'Assigned To' },
];

const LICENSE_SPEND_COLUMNS: DrilldownColumn[] = [
  { key: 'name', label: 'Software' },
  { key: 'plan', label: 'Plan' },
  { key: 'expiryDate', label: 'Expiry Date' },
  { key: 'totalCost', label: 'Total Cost' },
  { key: 'billingFrequency', label: 'Billing' },
];

const USER_COLUMNS: DrilldownColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' },
];

const RETURN_COLUMNS: DrilldownColumn[] = [
  { key: 'returnedBy', label: 'Returned By' },
  { key: 'condition', label: 'Condition' },
  { key: 'quantity', label: 'Qty' },
  { key: 'createdAt', label: 'Date' },
];

const PURCHASE_COLUMNS: DrilldownColumn[] = [
  { key: 'name', label: 'Item' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'totalCost', label: 'Total Cost' },
  { key: 'purchaseDate', label: 'Purchase Date' },
];

const TURNOVER_COLUMNS: DrilldownColumn[] = [
  { key: 'name', label: 'Item' },
  { key: 'quantity', label: 'Qty Issued' },
  { key: 'transactionDate', label: 'Date' },
];

const AUDIT_COLUMNS: DrilldownColumn[] = [
  { key: 'action', label: 'Action' },
  { key: 'entityType', label: 'Entity' },
  { key: 'actorName', label: 'Actor' },
  { key: 'createdAt', label: 'When' },
];

const USEFUL_LIFE_COLUMNS: DrilldownColumn[] = [
  { key: 'assetTag', label: 'Asset Tag' },
  { key: 'name', label: 'Name' },
  { key: 'acquisitionDate', label: 'Purchase / Rental Start' },
  { key: 'usefulLifeYears', label: 'Useful Life (yrs)' },
  { key: 'condition', label: 'Condition' },
  { key: 'currentValue', label: 'Book Value' },
];

const OVERDUE_REFUNDABLE_COLUMNS: DrilldownColumn[] = [
  { key: 'itemName', label: 'Item' },
  { key: 'quantity', label: 'Qty' },
  { key: 'location', label: 'Location' },
  { key: 'department', label: 'Department' },
  { key: 'expectedReturnDate', label: 'Expected Return' },
];

const COMPLIANCE_RISK_COLUMNS: DrilldownColumn[] = [
  { key: 'softwareName', label: 'Software' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'complianceRisk', label: 'Risk' },
  { key: 'totalCost', label: 'Annualized Cost' },
];

export const KPI_DRILLDOWN_CONFIG: Record<string, KpiDrilldownConfig> = {
  'kpi-total-assets': { title: 'Total Assets', columns: ASSET_COLUMNS },
  'kpi-utilization': { title: 'Asset Utilization', columns: ASSET_COLUMNS },
  'kpi-active-assignments': { title: 'Active Assignments', columns: ASSIGNMENT_COLUMNS },
  'kpi-overdue': { title: 'Overdue Assignments', columns: ASSIGNMENT_COLUMNS },
  'kpi-license-seats': { title: 'License Seat Utilization', columns: LICENSE_COLUMNS },
  'kpi-low-stock': { title: 'Low Stock Items', columns: INVENTORY_COLUMNS },
  'kpi-warranty-status': { title: 'Warranty Status', columns: WARRANTY_COLUMNS },
  'kpi-book-value': { title: 'Asset Book Value', columns: BOOK_VALUE_COLUMNS },
  'kpi-new-assets': { title: 'New Assets This Month', columns: NEW_ASSET_COLUMNS },
  'kpi-mrc': { title: 'Rented Assets — Monthly Cost', columns: RENTED_ASSET_COLUMNS },
  'kpi-rental-cost-total': { title: 'Total Rental Cost (All Time)', columns: RENTAL_COST_COLUMNS },
  'kpi-license-expiry': { title: 'License Expiry', columns: LICENSE_SPEND_COLUMNS },
  'kpi-annual-license-spend': { title: 'Annual License Spend', columns: LICENSE_SPEND_COLUMNS },
  'kpi-workforce': { title: 'Workforce', columns: USER_COLUMNS },
  'kpi-return-rate': { title: 'Returns (Last 30 Days)', columns: RETURN_COLUMNS },
  'kpi-damaged-returns': { title: 'Damaged Returns', columns: RETURN_COLUMNS },
  'kpi-stock-health': { title: 'Consumable Stock Health', columns: INVENTORY_COLUMNS },
  'kpi-inventory-spend': { title: 'Inventory Spend', columns: PURCHASE_COLUMNS },
  'kpi-inventory-turnover': { title: 'Inventory Turnover', columns: TURNOVER_COLUMNS },
  'kpi-system-activity': { title: 'System Activity (24h)', columns: AUDIT_COLUMNS },
  'kpi-unassigned-assets': { title: 'Unassigned Assets', columns: ASSET_COLUMNS },
  'kpi-inactive-users': { title: 'Inactive Users', columns: USER_COLUMNS },
  // Release Defect fix (2026-07-23): backend cases for these three already
  // existed (see analytics-drilldown.service.ts) — only this config entry
  // was missing, which made KpiDetailModal render an empty body (no title,
  // no table) since it requires `config` to render anything at all.
  'kpi-useful-life': { title: 'Assets Near / Beyond Useful Life', columns: USEFUL_LIFE_COLUMNS },
  'kpi-overdue-refundable': { title: 'Overdue Refundable Returns', columns: OVERDUE_REFUNDABLE_COLUMNS },
  'kpi-compliance-risk': { title: 'License Compliance Risk Exposure', columns: COMPLIANCE_RISK_COLUMNS },
};
